import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Paper,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Autocomplete,
  SelectChangeEvent,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  AutoAwesome as AutoAwesomeIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import ResearchService from "../services/ResearchService";
import { ResearchReport, CreateResearchDTO, AppIdea } from "../types/research";
import { CreateAppDTO } from "../types/app";

const APP_REGISTRATION_PREFILL_KEY = "app_registration_prefill";
const APP_REGISTRATION_CATEGORIES = [
  "productivity",
  "social",
  "games",
  "education",
  "lifestyle",
  "finance",
  "health",
  "travel",
  "utilities",
  "business",
];

const APP_CATEGORIES = [
  "Health & Wellness",
  "Productivity",
  "Gaming",
  "Social Media",
  "Education",
  "Finance",
  "Travel",
  "Food & Drink",
  "Shopping",
  "Entertainment",
  "Lifestyle",
  "Business",
  "News",
  "Photography",
  "Music",
  "Sports",
  "Weather",
  "Utilities",
  "AI & Automation",
  "Creator Economy",
  "Remote Work",
  "Fitness",
  "Mental Health",
  "Meditation",
  "Dating",
  "Communication",
  "Navigation",
  "Maps",
  "Books",
  "Comics",
  "Podcasts",
  "Video Streaming",
  "NFT & Crypto",
  "Sustainability",
  "E-learning",
  "Project Management",
  "Design Tools",
  "Dev Tools",
  "Home Automation",
  "IoT",
];

const RESEARCH_MODELS = [
  { value: "", label: "Default (Gemini 2.5 Flash Lite)" },
  { value: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash Lite" },
  { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro" },
  { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash" },
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function AppIdeaResearch() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [savedReports, setSavedReports] = useState<ResearchReport[]>([]);
  const [currentReport, setCurrentReport] = useState<ResearchReport | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [category, setCategory] = useState("");
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState("");
  const [targetRegion, setTargetRegion] = useState("");
  const [timeRange, setTimeRange] = useState<"7d" | "30d" | "90d" | "12m">(
    "30d",
  );
  const [selectedModel, setSelectedModel] = useState<string>("");

  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<ResearchReport | null>(
    null,
  );

  useEffect(() => {
    if (user?.email) {
      loadSavedReports();
    }
  }, [user?.email]);

  const loadSavedReports = useCallback(async () => {
    try {
      const reports = await ResearchService.getResearchReports(user!.email);
      setSavedReports(reports);
    } catch (err) {
      console.error("Error loading reports:", err);
      setError("Failed to load research reports");
    }
  }, [user?.email]);

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !keywords.includes(keywordInput.trim())) {
      setKeywords([...keywords, keywordInput.trim()]);
      setKeywordInput("");
    }
  };

  const handleRemoveKeyword = (index: number) => {
    setKeywords(keywords.filter((_, i) => i !== index));
  };

  const handleConductResearch = async () => {
    if (!category || keywords.length === 0) {
      setError("Please enter a category and at least one keyword");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const query: CreateResearchDTO = {
        category,
        keywords,
        targetRegion: targetRegion || undefined,
        timeRange,
      };

      const report = await ResearchService.conductResearch(
        user!.email,
        query,
        selectedModel || undefined,
      );
      setCurrentReport(report);
      setTabValue(1); // Switch to results tab
    } catch (err) {
      console.error("Error conducting research:", err);
      setError("Failed to conduct research. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReport = async () => {
    if (!currentReport) return;

    try {
      await ResearchService.saveReport(currentReport);
      await loadSavedReports();
      setError(null);
      alert("Research report saved successfully!");
    } catch (err) {
      console.error("Error saving report:", err);
      setError("Failed to save research report");
    }
  };

  const handleDeleteReport = async () => {
    if (!reportToDelete) return;

    try {
      await ResearchService.deleteReport(reportToDelete.reportId, user!.email);
      await loadSavedReports();
      setDeleteDialogOpen(false);
      setReportToDelete(null);
    } catch (err) {
      console.error("Error deleting report:", err);
      setError("Failed to delete research report");
    }
  };

  const handleExportJSON = () => {
    if (!currentReport) return;
    const json = ResearchService.exportReportAsJSON(currentReport);
    const blob = new Blob([json], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `research-report-${currentReport.reportId}.json`;
    link.click();
  };

  const handleExportCSV = () => {
    if (!currentReport) return;
    const csv = ResearchService.exportIdeasAsCSV(currentReport.trendingIdeas);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `trending-ideas-${currentReport.reportId}.csv`;
    link.click();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "#4caf50"; // green
    if (score >= 60) return "#ff9800"; // orange
    return "#f44336"; // red
  };

  const getCompetitionColor = (competition: string) => {
    if (competition === "low") return "#4caf50";
    if (competition === "medium") return "#ff9800";
    return "#f44336";
  };

  const mapIdeaCategoryToRegistrationCategory = (value: string) => {
    const normalized = String(value || "")
      .trim()
      .toLowerCase();
    if (!normalized) return "productivity";

    if (APP_REGISTRATION_CATEGORIES.includes(normalized)) {
      return normalized;
    }

    if (
      normalized.includes("health") ||
      normalized.includes("fitness") ||
      normalized.includes("wellness")
    ) {
      return "health";
    }
    if (
      normalized.includes("social") ||
      normalized.includes("dating") ||
      normalized.includes("community")
    ) {
      return "social";
    }
    if (normalized.includes("game")) {
      return "games";
    }
    if (normalized.includes("edu") || normalized.includes("learn")) {
      return "education";
    }
    if (normalized.includes("finance") || normalized.includes("fintech")) {
      return "finance";
    }
    if (normalized.includes("travel")) {
      return "travel";
    }
    if (normalized.includes("business") || normalized.includes("b2b")) {
      return "business";
    }
    if (normalized.includes("utility") || normalized.includes("tool")) {
      return "utilities";
    }
    if (
      normalized.includes("lifestyle") ||
      normalized.includes("food") ||
      normalized.includes("entertainment")
    ) {
      return "lifestyle";
    }

    return "productivity";
  };

  const handleCreateAppFromIdea = (idea: AppIdea) => {
    if (!currentReport) return;

    const categoryForRegistration = mapIdeaCategoryToRegistrationCategory(
      idea.category || currentReport.query.category,
    );

    const prefill: Partial<CreateAppDTO> = {
      name: String(idea.title || "").trim(),
      description: String(idea.description || "").trim(),
      category: categoryForRegistration,
      platform: "both",
      storeUrl: "",
      androidStoreUrl: "",
      iosStoreUrl: "",
      storeId: "",
      privacyPolicyUrl: "",
      termsOfServiceUrl: "",
      researchContext: {
        whatItDoes: String(idea.description || "").trim(),
        targetDemographics: idea.targetAudience ? [idea.targetAudience] : [],
        monetization: idea.potentialRevenue ? [idea.potentialRevenue] : [],
        growthSignals: [
          `Trend score: ${idea.trendScore}/100`,
          `Market size: ${idea.marketSize}`,
        ],
        opportunities: [idea.validationNotes].filter(Boolean),
        risks:
          idea.competition === "high"
            ? [
                "High competition in this category. Differentiate positioning and feature set.",
              ]
            : [],
        strategicRecommendations:
          currentReport.recommendations?.slice(0, 4) || [],
        summary: currentReport.summary,
      },
    };

    localStorage.setItem(APP_REGISTRATION_PREFILL_KEY, JSON.stringify(prefill));
    navigate("/app-registration");
  };

  return (
    <Container maxWidth={false} sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        App Idea Research
      </Typography>
      <Typography variant="body1" color="textSecondary" paragraph>
        Discover trending app ideas, analyze market opportunities, and validate
        concepts using AI-powered research.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="research tabs"
        >
          <Tab label="New Research" id="tab-0" aria-controls="tabpanel-0" />
          <Tab
            label="Current Results"
            id="tab-1"
            aria-controls="tabpanel-1"
            disabled={!currentReport}
          />
          <Tab label="Saved Reports" id="tab-2" aria-controls="tabpanel-2" />
        </Tabs>
      </Box>

      {/* New Research Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Research Parameters
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Autocomplete
                  options={APP_CATEGORIES}
                  value={category}
                  onChange={(event, newValue) => setCategory(newValue || "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Category"
                      placeholder="Search or select a category"
                    />
                  )}
                  filterOptions={(options, state) => {
                    const inputValue = state.inputValue.toLowerCase();
                    return options.filter((option) =>
                      option.toLowerCase().includes(inputValue),
                    );
                  }}
                  freeSolo
                  fullWidth
                  sx={{ mb: 2 }}
                />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Keywords (Enter keywords to search for trends)
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                    <TextField
                      size="small"
                      placeholder="e.g., AI health tracking, mental wellness"
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyPress={(e) =>
                        e.key === "Enter" && handleAddKeyword()
                      }
                      fullWidth
                    />
                    <Button variant="outlined" onClick={handleAddKeyword}>
                      Add
                    </Button>
                  </Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {keywords.map((keyword, index) => (
                      <Chip
                        key={index}
                        label={keyword}
                        onDelete={() => handleRemoveKeyword(index)}
                      />
                    ))}
                  </Box>
                </Box>

                <TextField
                  fullWidth
                  label="Target Region (Optional)"
                  placeholder="e.g., North America, Europe, Asia"
                  value={targetRegion}
                  onChange={(e) => setTargetRegion(e.target.value)}
                  sx={{ mb: 2 }}
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Time Range</InputLabel>
                  <Select
                    value={timeRange}
                    onChange={(e: SelectChangeEvent) =>
                      setTimeRange(e.target.value as any)
                    }
                    label="Time Range"
                  >
                    <MenuItem value="7d">Last 7 Days</MenuItem>
                    <MenuItem value="30d">Last 30 Days</MenuItem>
                    <MenuItem value="90d">Last 90 Days</MenuItem>
                    <MenuItem value="12m">Last 12 Months</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>AI Model (Optional)</InputLabel>
                  <Select
                    value={selectedModel}
                    onChange={(e: SelectChangeEvent) =>
                      setSelectedModel(e.target.value)
                    }
                    label="AI Model (Optional)"
                  >
                    {RESEARCH_MODELS.map((model) => (
                      <MenuItem
                        key={model.value || "default"}
                        value={model.value}
                      >
                        {model.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<RefreshIcon />}
                  onClick={handleConductResearch}
                  disabled={loading}
                  size="large"
                >
                  {loading ? "Conducting Research..." : "Conduct Research"}
                </Button>

                {loading && (
                  <Box sx={{ mt: 2 }}>
                    <LinearProgress />
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mt: 1, textAlign: "center" }}
                    >
                      Analyzing trends and opportunities... This may take a
                      moment.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, backgroundColor: "action.hover" }}>
              <Typography variant="h6" gutterBottom>
                How It Works
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                <strong>1. Enter Parameters</strong> - Specify your category,
                keywords, and time range
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                <strong>2. AI Analysis</strong> - Gemini searches for market
                insights and trends
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                <strong>3. Get Results</strong> - View trending ideas,
                competitor analysis, and opportunity scores
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                <strong>4. Export & Save</strong> - Download results or save for
                future reference
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Results Tab */}
      <TabPanel value={tabValue} index={1}>
        {currentReport && (
          <Grid container spacing={3}>
            {/* Summary */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3, backgroundColor: "action.hover" }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "start",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Research Summary
                    </Typography>
                    <Typography variant="body2" paragraph>
                      {currentReport.summary}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveReport}
                      variant="contained"
                    >
                      Save
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={handleExportJSON}
                    >
                      JSON
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={handleExportCSV}
                    >
                      CSV
                    </Button>
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        variant="h3"
                        sx={{
                          color: getScoreColor(currentReport.opportunityScore),
                        }}
                      >
                        {currentReport.opportunityScore}
                      </Typography>
                      <Typography variant="caption">
                        Opportunity Score
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="h4">
                        {currentReport.trendingIdeas.length}
                      </Typography>
                      <Typography variant="caption">Trending Ideas</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="h4">
                        {currentReport.recommendations.length}
                      </Typography>
                      <Typography variant="caption">Recommendations</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Trending Ideas */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <TrendingUpIcon /> Trending Ideas
                </Typography>

                <Grid container spacing={2}>
                  {currentReport.trendingIdeas.map((idea, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Card>
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "start",
                              mb: 1,
                            }}
                          >
                            <Typography variant="h6">{idea.title}</Typography>
                            <Chip
                              label={`${idea.trendScore}`}
                              sx={{
                                backgroundColor: getScoreColor(idea.trendScore),
                                color: "white",
                              }}
                              size="small"
                            />
                          </Box>

                          <Typography
                            variant="body2"
                            color="textSecondary"
                            paragraph
                          >
                            {idea.description}
                          </Typography>

                          <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" display="block">
                              <strong>Target Audience:</strong>{" "}
                              {idea.targetAudience}
                            </Typography>
                            <Typography variant="caption" display="block">
                              <strong>Market Size:</strong> {idea.marketSize}
                            </Typography>
                            <Typography variant="caption" display="block">
                              <strong>Competition:</strong>
                              <Chip
                                label={idea.competition}
                                size="small"
                                sx={{
                                  ml: 1,
                                  backgroundColor: getCompetitionColor(
                                    idea.competition,
                                  ),
                                  color: "white",
                                }}
                              />
                            </Typography>
                            <Typography variant="caption" display="block">
                              <strong>Potential Revenue:</strong>{" "}
                              {idea.potentialRevenue}
                            </Typography>
                          </Box>

                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{ fontStyle: "italic" }}
                          >
                            {idea.validationNotes}
                          </Typography>
                        </CardContent>
                        <CardActions sx={{ pt: 0, px: 2, pb: 2 }}>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<AutoAwesomeIcon />}
                            onClick={() => handleCreateAppFromIdea(idea)}
                          >
                            Create App from Idea
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>

            {/* Market Insights */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Market Insights
                </Typography>
                <Box component="ul" sx={{ pl: 2 }}>
                  {currentReport.marketInsights.map((insight, index) => (
                    <Typography
                      key={index}
                      component="li"
                      variant="body2"
                      paragraph
                    >
                      {insight}
                    </Typography>
                  ))}
                </Box>
              </Paper>
            </Grid>

            {/* Risk Factors */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, backgroundColor: "action.hover" }}>
                <Typography variant="h6" gutterBottom>
                  Risk Factors
                </Typography>
                <Box component="ul" sx={{ pl: 2 }}>
                  {currentReport.riskFactors.map((risk, index) => (
                    <Typography
                      key={index}
                      component="li"
                      variant="body2"
                      paragraph
                    >
                      {risk}
                    </Typography>
                  ))}
                </Box>
              </Paper>
            </Grid>

            {/* Monetization Strategies */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Monetization Strategies
                </Typography>
                <Grid container spacing={2}>
                  {currentReport.monetizationStrategies.map(
                    (strategy, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Box
                          sx={{
                            p: 2,
                            border: "1px solid", borderColor: "divider",
                            borderRadius: 1,
                            backgroundColor: "action.hover",
                          }}
                        >
                          <Typography variant="body2">{strategy}</Typography>
                        </Box>
                      </Grid>
                    ),
                  )}
                </Grid>
              </Paper>
            </Grid>

            {/* Competitor Analysis */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Competitor Analysis
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "action.hover" }}>
                      <TableRow>
                        <TableCell>
                          <strong>Competitor</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Rating</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Downloads</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Price Model</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Key Strengths</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentReport.competitorAnalysis.map(
                        (competitor, index) => (
                          <TableRow key={index}>
                            <TableCell>{competitor.name}</TableCell>
                            <TableCell align="center">
                              {competitor.rating}/5
                            </TableCell>
                            <TableCell align="center">
                              {competitor.downloads}
                            </TableCell>
                            <TableCell>{competitor.priceModel}</TableCell>
                            <TableCell>
                              {competitor.strengths.join(", ")}
                            </TableCell>
                          </TableRow>
                        ),
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            {/* Recommendations */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3, backgroundColor: "action.hover" }}>
                <Typography variant="h6" gutterBottom>
                  Recommendations
                </Typography>
                <Box component="ol" sx={{ pl: 2 }}>
                  {currentReport.recommendations.map((rec, index) => (
                    <Typography
                      key={index}
                      component="li"
                      variant="body2"
                      paragraph
                    >
                      {rec}
                    </Typography>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* Saved Reports Tab */}
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={2}>
          {savedReports.length === 0 ? (
            <Grid item xs={12}>
              <Alert severity="info">
                No saved research reports yet. Conduct a research to get
                started.
              </Alert>
            </Grid>
          ) : (
            savedReports.map((report) => (
              <Grid item xs={12} md={6} key={report.reportId}>
                <Card>
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "start",
                        mb: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="h6">
                          {report.query.category}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {report.query.keywords.join(", ")}
                        </Typography>
                      </Box>
                      <Chip
                        label={`Score: ${report.opportunityScore}`}
                        sx={{
                          backgroundColor: getScoreColor(
                            report.opportunityScore,
                          ),
                          color: "white",
                        }}
                      />
                    </Box>

                    <Typography variant="body2" paragraph>
                      {report.summary.substring(0, 150)}...
                    </Typography>

                    <Box
                      sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}
                    >
                      <Chip
                        label={`${report.trendingIdeas.length} Ideas`}
                        variant="outlined"
                        size="small"
                      />
                      <Chip
                        label={`${report.competitorAnalysis.length} Competitors`}
                        variant="outlined"
                        size="small"
                      />
                      <Chip
                        label={`Saved ${
                          report.savedAt
                            ? new Date(report.savedAt).toLocaleDateString()
                            : "Today"
                        }`}
                        variant="outlined"
                        size="small"
                      />
                    </Box>

                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                    >
                      Research took {report.metadata.researchDuration}s
                    </Typography>
                  </CardContent>

                  <CardActions>
                    <Button
                      size="small"
                      onClick={() => {
                        setCurrentReport(report);
                        setTabValue(1);
                      }}
                    >
                      View Details
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        setReportToDelete(report);
                        setDeleteDialogOpen(true);
                      }}
                      startIcon={<DeleteIcon />}
                    >
                      Delete
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      </TabPanel>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Research Report</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete this research report? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteReport}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
